import React, { useState } from 'react';
import {
  Container, Typography, Box, Grid, Card, CardContent,
  TextField, Button, MenuItem, Divider, Chip
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';

const serviceOptions = [
  { label: 'Laptop / Mac Screen Replacement',     base: 79,  note: 'Parts + labor' },
  { label: 'Battery Replacement',                 base: 59,  note: 'Parts + labor' },
  { label: 'Virus Removal & Cleanup',             base: 49,  note: 'Remote or on-site' },
  { label: 'OS Reinstallation',                   base: 59,  note: 'Data preserved' },
  { label: 'Smartphone Screen Repair',            base: 69,  note: 'Parts + labor' },
  { label: 'Data Recovery',                       base: 99,  note: 'Starting price' },
  { label: 'Network Setup (Home)',                base: 99,  note: 'Router + devices' },
  { label: 'Network Setup (Business)',            base: 199, note: 'Custom scope' },
  { label: 'WiFi Optimization',                  base: 79,  note: 'Site survey included' },
  { label: 'Microsoft 365 Setup',                base: 199, note: 'Full tenant setup' },
  { label: 'Firewall Configuration',             base: 149, note: 'ASA / Pfsense / SonicWall' },
  { label: 'General IT Support (Remote)',        base: 49,  note: 'Per hour' },
  { label: 'General IT Support (On-Site)',       base: 89,  note: 'Per hour' },
];

const addons = [
  { label: 'Rush / Same-Day Service',   cost: 30 },
  { label: 'On-Site Visit (Lynn, MA)',  cost: 40 },
  { label: 'Data Backup Before Repair', cost: 20 },
  { label: 'Post-Repair Tune-Up',       cost: 25 },
];

function Quote() {
  const navigate = useNavigate();
  const [service, setService] = useState('');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const selectedService = serviceOptions.find((s) => s.label === service);
  const addonTotal = addons
    .filter((a) => selectedAddons.includes(a.label))
    .reduce((sum, a) => sum + a.cost, 0);
  const total = selectedService ? selectedService.base + addonTotal : 0;

  const toggleAddon = (label) => {
    setSelectedAddons((prev) =>
      prev.includes(label) ? prev.filter((a) => a !== label) : [...prev, label]
    );
  };

  const handleSubmit = () => {
    if (!service || !name || !email) return;
    setSubmitted(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: '8%', right: '4%',
        width: 380, height: 380,
        background: 'radial-gradient(circle, rgba(255,107,107,0.09) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, py: 10 }}>
        <Container maxWidth="md">

          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, display: 'block', mb: 2 }}>
              Instant Estimate
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, fontWeight: 800, color: '#fff', mb: 2 }}>
              Get a Free{' '}
              <Box component="span" className="gradient-text">Service Quote</Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', lineHeight: 1.8 }}>
              Select your service and add-ons to get an instant price estimate.
            </Typography>
          </Box>

          {!submitted ? (
            <Grid container spacing={4}>
              {/* Left: configurator */}
              <Grid item xs={12} md={7}>
                <Card sx={{
                  background: 'rgba(255,107,107,0.05)',
                  border: '1px solid rgba(255,107,107,0.12)',
                  borderRadius: '18px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <CardContent sx={{ p: 4 }}>

                    {/* Service selector */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                      1. Select a Service
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={service}
                      onChange={(e) => setService(e.target.value)}
                      placeholder="Choose a service"
                      sx={{
                        mb: 4,
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          background: 'rgba(255,255,255,0.04)',
                          '& fieldset': { borderColor: 'rgba(255,107,107,0.25)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,107,107,0.5)' },
                          '&.Mui-focused fieldset': { borderColor: '#ff6b6b' },
                        },
                        '& .MuiSelect-icon': { color: 'primary.main' },
                      }}
                    >
                      <MenuItem value="" disabled>Choose a service…</MenuItem>
                      {serviceOptions.map((opt) => (
                        <MenuItem key={opt.label} value={opt.label}>
                          {opt.label} — from ${opt.base}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Add-ons */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                      2. Add-Ons (optional)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
                      {addons.map((addon) => {
                        const active = selectedAddons.includes(addon.label);
                        return (
                          <Chip
                            key={addon.label}
                            label={`${addon.label} +$${addon.cost}`}
                            onClick={() => toggleAddon(addon.label)}
                            icon={active ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              background: active
                                ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)'
                                : 'rgba(255,107,107,0.08)',
                              color: active ? '#2d3436' : '#fff',
                              border: active ? 'none' : '1px solid rgba(255,107,107,0.2)',
                              cursor: 'pointer',
                              '& .MuiChip-icon': { color: '#2d3436' },
                            }}
                          />
                        );
                      })}
                    </Box>

                    {/* Contact info */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                      3. Your Info
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Your Name" value={name}
                          onChange={(e) => setName(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { color: '#fff', background: 'rgba(255,255,255,0.04)', '& fieldset': { borderColor: 'rgba(255,107,107,0.25)' }, '&:hover fieldset': { borderColor: 'rgba(255,107,107,0.5)' } } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Email Address" type="email" value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { color: '#fff', background: 'rgba(255,255,255,0.04)', '& fieldset': { borderColor: 'rgba(255,107,107,0.25)' }, '&:hover fieldset': { borderColor: 'rgba(255,107,107,0.5)' } } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth multiline rows={3} label="Additional Notes (optional)" value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { color: '#fff', background: 'rgba(255,255,255,0.04)', '& fieldset': { borderColor: 'rgba(255,107,107,0.25)' }, '&:hover fieldset': { borderColor: 'rgba(255,107,107,0.5)' } } }}
                        />
                      </Grid>
                    </Grid>

                  </CardContent>
                </Card>
              </Grid>

              {/* Right: estimate summary */}
              <Grid item xs={12} md={5}>
                <Card sx={{
                  background: 'rgba(255,107,107,0.06)',
                  border: '1px solid rgba(255,107,107,0.2)',
                  borderRadius: '18px',
                  backdropFilter: 'blur(10px)',
                  position: 'sticky', top: 100,
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <CalculateIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                        Your Estimate
                      </Typography>
                    </Box>

                    {selectedService ? (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">{selectedService.label}</Typography>
                            <Typography variant="body2" sx={{ color: '#fff' }}>${selectedService.base}</Typography>
                          </Box>
                          {addons.filter((a) => selectedAddons.includes(a.label)).map((a) => (
                            <Box key={a.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">{a.label}</Typography>
                              <Typography variant="body2" sx={{ color: '#fff' }}>+${a.cost}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Divider sx={{ borderColor: 'rgba(255,107,107,0.15)', mb: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                          <Typography sx={{ fontWeight: 700, color: '#fff' }}>Estimated Total</Typography>
                          <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '1.2rem' }}>${total}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                          Final price confirmed after free diagnostics. No surprise charges.
                        </Typography>
                      </>
                    ) : (
                      <Typography color="text.secondary" sx={{ mb: 4, fontSize: '0.9rem' }}>
                        Select a service to see your estimate.
                      </Typography>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={!service || !name || !email}
                      endIcon={<ArrowForwardIcon />}
                      onClick={handleSubmit}
                      sx={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
                        color: '#2d3436', fontWeight: 700,
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(255,107,107,0.3)' },
                        '&.Mui-disabled': { opacity: 0.4 },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Send Quote Request
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Card sx={{
              background: 'rgba(255,107,107,0.05)',
              border: '1px solid rgba(255,107,107,0.2)',
              borderRadius: '20px',
              backdropFilter: 'blur(10px)',
              textAlign: 'center', p: { xs: 4, md: 6 },
            }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 700, mb: 1.5 }}>
                Quote Request Sent!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Thanks, <strong style={{ color: '#fff' }}>{name}</strong>! We'll review your request and get back to you at{' '}
                <strong style={{ color: '#ff6b6b' }}>{email}</strong> within a few hours.
              </Typography>
              {selectedService && (
                <Typography color="text.secondary" sx={{ mb: 4 }}>
                  Estimated price for <strong style={{ color: '#fff' }}>{service}</strong>:{' '}
                  <strong style={{ color: '#ff6b6b' }}>from ${total}</strong>
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/book-service')}
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
                    color: '#2d3436', fontWeight: 700, px: 4,
                  }}
                >
                  Book Now
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => { setSubmitted(false); setService(''); setSelectedAddons([]); setName(''); setEmail(''); setNotes(''); }}
                  sx={{ borderColor: 'rgba(255,107,107,0.4)', color: '#fff', px: 4 }}
                >
                  New Quote
                </Button>
              </Box>
            </Card>
          )}

        </Container>
      </Box>
    </Box>
  );
}

export default Quote;
